import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import EditingReturnBanner, { backToLabelFor } from '../projects/EditingReturnBanner'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import MultiSelect from '../ui/MultiSelect'
import InlineSelect from '../ui/InlineSelect'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import { useCatalog } from '../../contexts/CatalogContext'

const EMPTY_PREHEAT_PAYLOAD = JSON.stringify({
  preheatMin: '',
  preheatMax: '',
  interpassMin: '',
  interpassMax: '',
  preheatUnit: '°F',
  interpassUnit: '°F',
  heatingMethod: [] as string[],
  controlMethod: [] as string[],
  preheatNote: '',
  interpassNote: ''
})

export default function WpsPreheatDetails() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  // Slice 09 of pqr-preview-field-navigation. Entry points:
  //   - Notes row "Preheat Method" -> ?focusField=method
  //   - Welding Parameters column "Preheat / Interpass (\u00b0F)" (whole
  //     column) -> ?focusField=preheat
  // The marker is resolved by `[data-focus-field="<key>"]`; we scroll
  // it into view + flash + try to focus an inner control. `returnTo`
  // rewires the page header's Back button so the user returns to the
  // PQR preview.
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const formContainerRef = useRef<HTMLDivElement>(null)
  useFieldFocusHandler({
    ready: true,
    resolveTarget: (params) => {
      if (!params.focusField) return null
      const root = formContainerRef.current ?? document
      const el = root.querySelector(
        `[data-focus-field="${params.focusField}"]`
      ) as HTMLElement | null
      if (!el) return null
      const inner = el.querySelector(
        'input, select, textarea, button, [role="button"]'
      ) as HTMLElement | null
      return inner ?? el
    }
  })
  const { getProject } = useProjects()
  const { getSection, updateSection } = useWpsSections()
  const { getLabelsMerged, addLabel } = useCatalog()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)

  const [preheatMin, setPreheatMin] = useState('')
  const [preheatMax, setPreheatMax] = useState('')
  const [interpassMin, setInterpassMin] = useState('')
  const [interpassMax, setInterpassMax] = useState('')
  const [preheatUnit, setPreheatUnit] = useState('°F')
  const [interpassUnit, setInterpassUnit] = useState('°F')
  const [heatingMethod, setHeatingMethod] = useState<string[]>([])
  const [controlMethod, setControlMethod] = useState<string[]>([])
  const [preheatNote, setPreheatNote] = useState('')
  const [interpassNote, setInterpassNote] = useState('')
  const [savedPreheatJson, setSavedPreheatJson] = useState('')
  const [saving, setSaving] = useState(false)

  const preheatPayload = useMemo(
    () =>
      JSON.stringify({
        preheatMin,
        preheatMax,
        interpassMin,
        interpassMax,
        preheatUnit,
        interpassUnit,
        heatingMethod,
        controlMethod,
        preheatNote,
        interpassNote
      }),
    [
      preheatMin,
      preheatMax,
      interpassMin,
      interpassMax,
      preheatUnit,
      interpassUnit,
      heatingMethod,
      controlMethod,
      preheatNote,
      interpassNote
    ]
  )

  const isDirty = useMemo(() => {
    if (!savedPreheatJson) return preheatPayload !== EMPTY_PREHEAT_PAYLOAD
    return preheatPayload !== savedPreheatJson
  }, [preheatPayload, savedPreheatJson])

  const heatingOptions = useMemo(
    () => getLabelsMerged('preheat_heating_method', heatingMethod),
    [getLabelsMerged, heatingMethod]
  )
  const controlOptions = useMemo(
    () => getLabelsMerged('preheat_control_method', controlMethod),
    [getLabelsMerged, controlMethod]
  )

  useEffect(() => {
    if (!pipeId) return
    const normalizeUnit = (value: string | undefined) =>
      value === '°C' || value === '°F' ? value : '°F'
    const parsed = getSection('preheat')
    if (parsed) {
      try {
        const next = {
          preheatMin: parsed.preheatMin || '',
          preheatMax: parsed.preheatMax || '',
          interpassMin: parsed.interpassMin || '',
          interpassMax: parsed.interpassMax || '',
          preheatUnit: normalizeUnit(parsed.preheatUnit),
          interpassUnit: normalizeUnit(parsed.interpassUnit),
          heatingMethod: parsed.heatingMethod || [],
          controlMethod: parsed.controlMethod || [],
          preheatNote: parsed.preheatNote || '',
          interpassNote: parsed.interpassNote || ''
        }
        setPreheatMin(next.preheatMin)
        setPreheatMax(next.preheatMax)
        setInterpassMin(next.interpassMin)
        setInterpassMax(next.interpassMax)
        setPreheatUnit(next.preheatUnit)
        setInterpassUnit(next.interpassUnit)
        setHeatingMethod(next.heatingMethod)
        setControlMethod(next.controlMethod)
        setPreheatNote(next.preheatNote)
        setInterpassNote(next.interpassNote)
        setSavedPreheatJson(JSON.stringify(next))
      } catch (error) {
        // ignore
      }
    }
  }, [pipeId, getSection])

  useEffect(() => {
    setSavedPreheatJson('')
  }, [pipeId])

  const handleSave = async () => {
    if (!pipeId || !isDirty || saving) return
    const body = {
      preheatMin,
      preheatMax,
      interpassMin,
      interpassMax,
      preheatUnit,
      interpassUnit,
      heatingMethod,
      controlMethod,
      preheatNote,
      interpassNote
    }
    setSaving(true)
    try {
      await updateSection('preheat', body)
      setSavedPreheatJson(JSON.stringify(body))
      toast.success('Preheat details saved')
      navigate(-1)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save preheat details')
    } finally {
      setSaving(false)
    }
  }

  if (!project || !pipeSet) {
    return (
      <Card title="Preheat not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  const pageHeaderBackTo = returnTo
    ? { to: returnTo, label: backToLabelFor(returnTo) }
    : { onClick: () => navigate(-1), label: 'Back' }

  return (
    <div className="space-y-6" ref={formContainerRef}>
      <EditingReturnBanner returnTo={returnTo} contextLabel="Preheat / Interpass" />
      <PageHeader
        backTo={pageHeaderBackTo}
        title={
          <>
            <span>Preheat / Interpass</span>
            <span className="text-heritage-grey/60">•</span>
            <span className="truncate text-text-grey">{project.name}</span>
          </>
        }
        subtitle={
          <>
            <span>{(project.endClients?.[0] ?? project.client)} • {project.location}</span>
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
          </>
        }
        actions={
          <>
            <Button disabled={!isDirty || saving} loading={saving} onClick={() => void handleSave()}>
              Save
            </Button>
          </>
        }
      />

      <div className="min-w-0 space-y-6 border-0 p-0">
      <div
        data-focus-field="preheat"
        className="rounded-lg border border-gray-200 bg-white p-4"
      >
        <div className="grid gap-3 text-xs text-gray-600">
          <div className="grid gap-3 md:grid-cols-[1.5fr_1fr_1fr] md:items-center">
            <p className="font-medium text-gray-700">Preheat Temperature</p>
            <div>
              <label className="text-[11px] text-gray-500">Min</label>
              <div className="mt-1 flex rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500 divide-x divide-gray-200">
                <input
                  className="w-full border-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
                  value={preheatMin}
                  onChange={(event) => setPreheatMin(event.target.value)}
                  placeholder="Min"
                />
                <InlineSelect
                  value={preheatUnit}
                  options={[
                    { value: '°F', label: '°F' },
                    { value: '°C', label: '°C' }
                  ]}
                  onChange={setPreheatUnit}
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Max</label>
              <div className="mt-1 flex rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500 divide-x divide-gray-200">
                <input
                  className="w-full border-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
                  value={preheatMax}
                  onChange={(event) => setPreheatMax(event.target.value)}
                  placeholder="Max"
                />
                <InlineSelect
                  value={preheatUnit}
                  options={[
                    { value: '°F', label: '°F' },
                    { value: '°C', label: '°C' }
                  ]}
                  onChange={setPreheatUnit}
                />
              </div>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-[1.5fr_1fr_1fr] md:items-center">
            <p className="font-medium text-gray-700">Interpass Temperature</p>
            <div>
              <label className="text-[11px] text-gray-500">Min</label>
              <div className="mt-1 flex rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500 divide-x divide-gray-200">
                <input
                  className="w-full border-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
                  value={interpassMin}
                  onChange={(event) => setInterpassMin(event.target.value)}
                  placeholder="Min"
                />
                <InlineSelect
                  value={interpassUnit}
                  options={[
                    { value: '°F', label: '°F' },
                    { value: '°C', label: '°C' }
                  ]}
                  onChange={setInterpassUnit}
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Max</label>
              <div className="mt-1 flex rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500 divide-x divide-gray-200">
                <input
                  className="w-full border-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
                  value={interpassMax}
                  onChange={(event) => setInterpassMax(event.target.value)}
                  placeholder="Max"
                />
                <InlineSelect
                  value={interpassUnit}
                  options={[
                    { value: '°F', label: '°F' },
                    { value: '°C', label: '°C' }
                  ]}
                  onChange={setInterpassUnit}
                />
              </div>
            </div>
          </div>
          <div
            data-focus-field="method"
            className="grid gap-3 md:grid-cols-[1.5fr_2fr] md:items-center"
          >
            <p className="font-medium text-gray-700">Heating Method</p>
            <MultiSelect
              values={heatingMethod}
              options={heatingOptions}
              placeholder="Select method..."
              addLabel="Add Method"
              onChange={setHeatingMethod}
              onCreateOption={async (value) => {
                await addLabel('preheat_heating_method', value)
                setHeatingMethod((prev) => (prev.includes(value) ? prev : [...prev, value]))
              }}
              className="mt-1"
            />
          </div>
          <div className="grid gap-3 md:grid-cols-[1.5fr_2fr] md:items-center">
            <p className="font-medium text-gray-700">Control Method</p>
            <MultiSelect
              values={controlMethod}
              options={controlOptions}
              placeholder="Select method..."
              addLabel="Add Method"
              onChange={setControlMethod}
              onCreateOption={async (value) => {
                await addLabel('preheat_control_method', value)
                setControlMethod((prev) => (prev.includes(value) ? prev : [...prev, value]))
              }}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-xs font-medium text-gray-500">
            Preheat guidance
          </label>
          <textarea
            className="input-primary mt-1 h-24"
            value={preheatNote}
            onChange={(event) => setPreheatNote(event.target.value)}
            placeholder="e.g. Measure at 2 locations equally spaced around the circumference..."
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">
            Interpass guidance
          </label>
          <textarea
            className="input-primary mt-1 h-24"
            value={interpassNote}
            onChange={(event) => setInterpassNote(event.target.value)}
            placeholder="e.g. Measure at toe of bevel just before next pass..."
          />
        </div>
      </div>
      </div>
    </div>
  )
}
